/**
 * The code half of history (RFC 0313 3h, 3i, 3n), read from the project's git
 * server on this device.
 *
 * RFC 0316's branch watcher would put commits on the log as events; until it
 * runs, git is the record, and this asks it directly. Read-only: code is
 * undone by a commit, never from a history row.
 *
 * A service because two features read it: a project's history and a session's
 * Changes.
 */

import { useCallback, useEffect, useState } from 'react'
import { fetchVcsFromServer } from '../../sdk/vcs'

export interface CodeCommit {
  sha: string
  shortSha: string
  author: string
  /** ISO 8601. */
  date: string
  subject: string
  files: number
  insertions: number
  deletions: number
  /** The branch it was reached by, on the project timeline only. */
  branch?: string
}

export interface CodeFileChange {
  path: string
  status: 'added' | 'modified' | 'deleted' | 'renamed'
  /** Lines added and removed, counted off the diff's hunks. */
  insertions: number
  deletions: number
}

/** How often an open history asks git again, so an agent's commit shows. */
export const CODE_POLL_MS = 10_000

/**
 * A read of the git server that re-asks on an interval while mounted. [] while
 * unknown, on error, and for a project with no checkout here.
 */
function usePolled<T>(
  key: string | null,
  read: () => Promise<T[]>,
): { value: T[]; reload: () => void } {
  const [state, setState] = useState<{ key: string | null; value: T[] }>({
    key: null,
    value: [],
  })
  const [asked, setAsked] = useState(0)
  // `read` changes identity every render; `key` says what it reads.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by `key`
  useEffect(() => {
    if (!key) {
      setState({ key: null, value: [] })
      return
    }
    let live = true
    let last = ''
    let first = true
    const once = () => {
      // A hidden window asks nothing: the working-tree read is a whole diff.
      if (!first && document.visibilityState === 'hidden') return
      first = false
      void read().then(
        (v) => {
          const next = Array.isArray(v) ? v : []
          // Unchanged answers do not re-render the list every ten seconds.
          const seen = JSON.stringify(next)
          if (live && seen !== last) {
            last = seen
            setState({ key, value: next })
          }
        },
        // One failed ask keeps what was shown; a project with no checkout
        // never had anything to show.
        () => {},
      )
    }
    once()
    const timer = setInterval(once, CODE_POLL_MS)
    return () => {
      live = false
      clearInterval(timer)
    }
  }, [key, asked])
  const reload = useCallback(() => setAsked((n) => n + 1), [])
  return { value: state.key === key ? state.value : [], reload }
}

/** The checkout's commits from the last `days`, across its local branches. */
export function useRecentCommits(
  projectId: string | undefined,
  days = 14,
  sourceId?: string,
): CodeCommit[] {
  return usePolled<CodeCommit>(
    projectId ? `log:${projectId}:${sourceId ?? 'default'}:${days}` : null,
    () =>
      fetchVcsFromServer(
        'log',
        [days, 50],
        projectId as string,
        sourceId,
      ) as Promise<CodeCommit[]>,
  ).value
}

/** The commits that changed one file, newest first. `ref` is a branch or
 *  sha; absent reads the checkout's HEAD. */
export function useFileCommits(
  projectId: string | undefined,
  path: string | undefined,
  ref?: string,
  sourceId?: string,
): CodeCommit[] {
  return usePolled<CodeCommit>(
    projectId && path
      ? `fileLog:${projectId}:${sourceId ?? 'default'}:${ref ?? ''}:${path}`
      : null,
    () =>
      fetchVcsFromServer(
        'fileLog',
        [path, ref ?? ''],
        projectId as string,
        sourceId,
      ) as Promise<CodeCommit[]>,
  ).value
}

/** The commits `branch` made since `base`, newest first. */
export function useBranchCommits(
  projectId: string | undefined,
  branch: string | undefined,
  base: string | undefined,
  sourceId?: string,
): CodeCommit[] {
  return usePolled<CodeCommit>(
    projectId && branch && base
      ? `commits:${projectId}:${sourceId ?? 'default'}:${branch}:${base}`
      : null,
    () =>
      fetchVcsFromServer(
        'commits',
        [branch, base, 50],
        projectId as string,
        sourceId,
      ) as Promise<CodeCommit[]>,
  ).value
}

/**
 * Files in a diff answer, without the server's notes. A note ("diff truncated
 * at…") comes back as a row with a sentence for a path and no hunks; a real
 * file that was added, deleted or renamed may have none either.
 */
export function filesOf(
  diff: {
    path: string
    status: CodeFileChange['status']
    hunks?: { lines?: string[] }[]
  }[],
): CodeFileChange[] {
  return diff
    .filter((f) => f.path && (f.status !== 'modified' || f.hunks?.length))
    .map((f) => {
      let insertions = 0
      let deletions = 0
      for (const h of f.hunks ?? [])
        for (const line of h.lines ?? []) {
          if (line.startsWith('+')) insertions++
          else if (line.startsWith('-')) deletions++
        }
      return { path: f.path, status: f.status, insertions, deletions }
    })
}

/** Lines added and removed across files, for a row's summary. */
export function lineCounts(files: CodeFileChange[]): {
  insertions: number
  deletions: number
} {
  return files.reduce(
    (n, f) => ({
      insertions: n.insertions + f.insertions,
      deletions: n.deletions + f.deletions,
    }),
    { insertions: 0, deletions: 0 },
  )
}

/** What the checkout has changed and not committed. */
export function useWorkingFiles(
  projectId: string | undefined,
  sourceId?: string,
): CodeFileChange[] {
  return usePolled<CodeFileChange>(
    projectId ? `working:${projectId}:${sourceId ?? 'default'}` : null,
    async () =>
      filesOf(
        (await fetchVcsFromServer(
          'working',
          [],
          projectId as string,
          sourceId,
        )) as Parameters<typeof filesOf>[0],
      ),
  ).value
}

/** The files one commit changed. A sha read, so answered from memory after
 *  the first time. */
export async function commitFiles(
  projectId: string,
  sha: string,
  sourceId?: string,
): Promise<CodeFileChange[]> {
  return filesOf(
    (await fetchVcsFromServer(
      'commitDiff',
      [sha],
      projectId,
      sourceId,
    )) as Parameters<typeof filesOf>[0],
  )
}
